import { FolderKanban, Edit, Trash2 } from "lucide-react";
import type { Project, ProjectType, ProjectStatus, ResponsiblePerson, ClientType, ProjectKind } from "@shared/schema";
import { projectTypeOptions, projectStatusOptions, responsiblePersonOptions, clientTypeOptions, projectKindOptions } from "@shared/schema";
import type { DataTableConfig, ColorMapping } from "@/components/data-table/types";

// Color mappings for project type pills
export const projectTypeColors: Record<ProjectType, ColorMapping> = {
  "WOD-KAN": { bg: "bg-blue-500", text: "text-white" },
  "Pompa ciepła": { bg: "bg-orange-500", text: "text-white" },
  "Więcej instalacji": { bg: "bg-red-500", text: "text-white" },
  "Wentylacja": { bg: "bg-[#C6DBE1]", text: "text-black" },
  "Przegląd techniczny": { bg: "bg-gray-500", text: "text-white" },
  "Klimatyzacja": { bg: "bg-violet-500", text: "text-white" },
  "PV": { bg: "bg-yellow-500", text: "text-black" },
  "Podłogówka": { bg: "bg-blue-400", text: "text-white" },
  "Serwis (naprawy)": { bg: "bg-gray-400", text: "text-white" },
  "Projektowanie": { bg: "bg-gray-600", text: "text-white" },
  "Inny": { bg: "bg-gray-300", text: "text-black" },
  "Administracyjny": { bg: "bg-slate-500", text: "text-white" },
};

// Color mappings for project status pills
export const projectStatusColors: Record<ProjectStatus, ColorMapping> = {
  "W przygotowaniu": { bg: "bg-sky-300", text: "text-black" },
  "W trakcie": { bg: "bg-green-500", text: "text-white" },
  "Wstrzymany": { bg: "bg-yellow-500", text: "text-black" },
  "Zakończony": { bg: "bg-red-500", text: "text-white" },
  "Administracyjny": { bg: "bg-gray-500", text: "text-white" },
};

// Color mappings for responsible person pills
export const responsiblePersonColors: Record<ResponsiblePerson, ColorMapping> = {
  "Basia": { bg: "bg-yellow-500", text: "text-black" },
  "Ala": { bg: "bg-green-500", text: "text-white" },
  "Grzesiek": { bg: "bg-blue-500", text: "text-white" },
  "Michał": { bg: "bg-red-500", text: "text-white" },
};

// Color mappings for client type (reuse from clients config)
export const clientTypeColors: Record<ClientType, ColorMapping> = {
  Komercyjny: { bg: "bg-green-500", text: "text-white" },
  Indywidualny: { bg: "bg-yellow-500", text: "text-black" },
  Administracyjny: { bg: "bg-red-500", text: "text-white" },
};

// Color mappings for project kind pills
export const projectKindColors: Record<ProjectKind, ColorMapping> = {
  "Zewnętrzny": { bg: "bg-blue-600", text: "text-white" },
  "Wewnętrzny": { bg: "bg-purple-600", text: "text-white" },
};

export const projectsTableConfig: DataTableConfig<Project> = {
  entityName: {
    singular: "projekt",
    plural: "projektów",
    genitiveSingular: "projektu",
  },

  testIdPrefix: "project",
  defaultSortField: "nrPorzadkowy",

  apiEndpoint: "/api/projects",
  historyEndpoint: "/api/projects/:id/history",

  columns: [
    {
      key: "nrPorzadkowy",
      header: "Nr porządkowy",
      width: "w-[100px]",
      editable: false,
    },
    {
      key: "rodzajProjektu",
      header: "Rodzaj",
      width: "w-[110px]",
      editable: true,
      type: "select",
      options: projectKindOptions,
      colorMapping: projectKindColors,
      testIdType: "projectKind",
    },
    {
      key: "klient",
      header: "Klient",
      width: "w-[160px]",
      editable: true,
      type: "clientSearch",
    },
    {
      key: "nrPorzadkowyKlienta",
      header: "Nr porządkowy klienta",
      width: "w-[140px]",
      editable: false,
    },
    {
      key: "nazwaRoboczaProjektu",
      header: "Nazwa robocza projektu",
      width: "w-[180px]",
      editable: true,
    },
    {
      key: "typKlienta",
      header: "Typ klienta",
      width: "w-[120px]",
      editable: true,
      type: "select",
      options: clientTypeOptions,
      colorMapping: clientTypeColors,
      testIdType: "clientType",
    },
    {
      key: "typProjektu",
      header: "Typ projektu",
      width: "w-[140px]",
      editable: true,
      type: "select",
      options: projectTypeOptions,
      colorMapping: projectTypeColors,
      testIdType: "projectType",
      filterable: true,
      filterLabel: "Typ projektu",
    },
    {
      key: "statusProjektu",
      header: "Status projektu",
      width: "w-[130px]",
      editable: true,
      type: "select",
      options: projectStatusOptions,
      colorMapping: projectStatusColors,
      testIdType: "projectStatus",
      filterable: true,
      filterLabel: "Status projektu",
    },
    {
      key: "osobaSprzedaz",
      header: "Osoba odpowiedzialna za sprzedaż",
      width: "w-[180px]",
      editable: true,
      type: "select",
      options: responsiblePersonOptions,
      colorMapping: responsiblePersonColors,
      testIdType: "personSales",
    },
    {
      key: "osobaWykonastwo",
      header: "Osoba odpowiedzialny za wykonastwo",
      width: "w-[200px]",
      editable: true,
      type: "select",
      options: responsiblePersonOptions,
      colorMapping: responsiblePersonColors,
      testIdType: "personExecution",
    },
    {
      key: "wartoscUmowyNetto",
      header: "Wartość umowy netto",
      width: "w-[140px]",
      editable: true,
      type: "currency",
    },
    {
      key: "planowanyKosztTowaru",
      header: "Planowany koszt towaru",
      width: "w-[150px]",
      editable: true,
      type: "currency",
    },
    {
      key: "planowaneKosztyWykonastwa",
      header: "Planowane koszta wykonastwa",
      width: "w-[170px]",
      editable: true,
      type: "currency",
    },
    {
      key: "narzutBezpieczenstwa",
      header: "Narzut bezpieczeństwa",
      width: "w-[150px]",
      editable: true,
      type: "currency",
    },
    {
      key: "planowanyZysk",
      header: "Planowany zysk",
      width: "w-[130px]",
      editable: false,
      type: "currency",
      tooltip: "Wartość obliczana automatycznie: Wartość umowy netto − Planowany koszt towaru − Planowane koszty wykonastwa − Narzut bezpieczeństwa",
    },
    {
      key: "wartoscPracDodatkowych",
      header: "Wartość prac dodatkowych",
      width: "w-[160px]",
      editable: true,
      type: "currency",
    },
    {
      key: "podpisanieUmowy",
      header: "Podpisanie umowy?",
      width: "w-[130px]",
      editable: true,
      type: "checkbox",
    },
    {
      key: "dataPodpisaniaUmowy",
      header: "Data podpisania umowy",
      width: "w-[150px]",
      editable: true,
      type: "date",
    },
    {
      key: "planowanyPoczatekRealizacji",
      header: "Planowany początek realizacji",
      width: "w-[170px]",
      editable: true,
      type: "date",
    },
    {
      key: "planowanyKoniecRealizacji",
      header: "Planowany koniec realizacji",
      width: "w-[160px]",
      editable: true,
      type: "date",
    },
    {
      key: "faktycznyPoczatekRealizacji",
      header: "Faktyczny początek realizacji",
      width: "w-[170px]",
      editable: true,
      type: "date",
    },
    {
      key: "faktycznyKoniecRealizacji",
      header: "Faktyczny koniec realizacji",
      width: "w-[160px]",
      editable: true,
      type: "date",
    },
  ],

  defaultValues: {
    nrPorzadkowy: undefined, // Auto-generated server-side
    rodzajProjektu: "Zewnętrzny" as ProjectKind,
    nrPorzadkowyKlienta: null,
    nazwaRoboczaProjektu: null,
    klient: null,
    typKlienta: null,
    typProjektu: null,
    statusProjektu: "W przygotowaniu" as ProjectStatus,
    osobaSprzedaz: null,
    osobaWykonastwo: null,
    wartoscUmowyNetto: null,
    planowanyKosztTowaru: null,
    planowaneKosztyWykonastwa: null,
    narzutBezpieczenstwa: null,
    planowanyZysk: null,
    wartoscPracDodatkowych: null,
    podpisanieUmowy: false,
    dataPodpisaniaUmowy: null,
    planowanyPoczatekRealizacji: null,
    planowanyKoniecRealizacji: null,
    faktycznyPoczatekRealizacji: null,
    faktycznyKoniecRealizacji: null,
    createdAt: new Date(),
    updatedAt: new Date(),
  },

  displayIdField: "nrPorzadkowy",
  nameField: "nazwaRoboczaProjektu",

  searchableFields: [
    "nrPorzadkowy",
    "nrPorzadkowyKlienta",
    "nazwaRoboczaProjektu",
    "klient",
  ],

  filters: [
    {
      key: "typProjektu",
      label: "Typ projektu",
      options: projectTypeOptions,
      allLabel: "Typ projektu",
      testId: "project-type",
      renderAs: "select",
      showEmptyOption: true,
    },
    {
      key: "statusProjektu",
      label: "Status projektu",
      options: projectStatusOptions,
      allLabel: "Wszystkie statusy",
      testId: "project-status",
      renderAs: "select",
      showEmptyOption: true,
    },
  ],

  bulkActions: [
    {
      id: "editKind",
      label: "Zmień rodzaj",
      icon: Edit,
      field: "rodzajProjektu",
      options: [...projectKindOptions],
      testId: "edit-kind",
    },
    {
      id: "editStatus",
      label: "Zmień status",
      icon: Edit,
      field: "statusProjektu",
      options: [...projectStatusOptions],
      testId: "edit-status",
    },
    {
      id: "editType",
      label: "Zmień typ",
      icon: Edit,
      field: "typProjektu",
      options: [...projectTypeOptions],
      testId: "edit-type",
    },
    {
      id: "delete",
      label: "Usuń wybrane",
      icon: Trash2,
      variant: "destructive",
      isDelete: true,
    },
  ],

  toastMessages: {
    createSuccess: "Projekt został dodany",
    createError: "Nie udało się dodać projektu",
    updateSuccess: "Zmiany zostały zapisane",
    updateError: "Nie udało się zapisać zmian",
    deleteSuccess: "Projekt został usunięty",
    deleteError: "Nie udało się usunąć projektu",
    bulkDeleteSuccess: (count) =>
      `Usunięto ${count} ${count === 1 ? "projekt" : "projektów"}`,
    bulkDeleteError: "Nie udało się usunąć wybranych projektów",
    bulkEditSuccess: (count) =>
      `Zaktualizowano ${count} ${count === 1 ? "projekt" : "projektów"}`,
    bulkEditError: "Nie udało się zaktualizować wybranych projektów",
  },

  emptyState: {
    title: "Brak projektów",
    description:
      "Rozpocznij dodawanie projektów do systemu AirAddiction. Kliknij przycisk poniżej, aby dodać pierwszy projekt.",
    addButtonText: "Dodaj pierwszy projekt",
    icon: FolderKanban,
  },

  keyboardShortcutsText:
    "Tab - następna komórka | Shift+Tab - poprzednia komórka | Enter - następny wiersz | Escape - anuluj edycję | ↑↓ - zmiana wiersza | Kliknij nagłówek - sortuj",
};
